import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    CircularProgress,
    FilledInput,
    FormControl,
    FormHelperText,
    Grid,
    withStyles,
    NativeSelect,
} from '@material-ui/core';
import styles from './styles';
import { ExpandMore } from '@material-ui/icons';
import { has } from 'ramda';

export const SIZE_COMPACT = 'compact';
export const SIZE_LARGE = 'large';
export const SIZES = [SIZE_COMPACT, SIZE_LARGE];

/**
 * AdminAutocomplete is a component that renders a native select input with options
 * and provides the ability for type ahead / autocomplete. Note, this currently requires the use of
 * NativeSelect as the Select component does not support the ability to type in a value in current
 * version of MUI.
 */
class AdminAutocomplete extends Component {
    render() {
        const {
            classes,
            type,
            options,
            value,
            loading,
            error,
            disabled,
            FormControlProps = {},
            ...props
        } = this.props;

        return loading && options ? (
            <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                className={classes[`${type}Root`]}
            >
                <Grid item>
                    <CircularProgress
                        size={type === SIZE_LARGE ? 40 : 20}
                        color="secondary"
                    />
                </Grid>
            </Grid>
        ) : (
            <FormControl {...FormControlProps}>
                <NativeSelect
                    value={value}
                    name="age"
                    disabled={disabled}
                    input={
                        <FilledInput
                            fullWidth
                            classes={{
                                input: classes.select,
                            }}
                        />
                    }
                    IconComponent={ExpandMore}
                    disableUnderline
                    className={classes.selectEmpty}
                    classes={{
                        root: classNames([
                            classes.root,
                            classes[`${type}Root`],
                            error ? classes.error : null,
                        ]),
                        icon: classNames([
                            classes.icon,
                            type === SIZE_COMPACT ? classes.compactIcon : null,
                            props.disabled ? classes.disabledIcon : null,
                        ]),
                        select: styles.select,
                    }}
                    {...props}
                    error={!!error}
                >
                    {options &&
                        options.map((option) => (
                            <option
                                className={classNames([
                                    classes.menuItem,
                                    type === SIZE_COMPACT
                                        ? classes.compactMenuItem
                                        : null,
                                ])}
                                key={option.value || option}
                                value={
                                    has('value', option) ? option.value : option
                                }
                            >
                                {option.label || option}
                            </option>
                        ))}
                </NativeSelect>
                {error ? (
                    <FormHelperText error className={classes.errorText}>
                        {' '}
                        {error}{' '}
                    </FormHelperText>
                ) : null}
            </FormControl>
        );
    }
}

AdminAutocomplete.propTypes = {
    // From withStyles we expect to get classes
    classes: PropTypes.object.isRequired,

    // The type of dropdown to render.
    type: PropTypes.oneOf(SIZES),

    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ]),

    // The options that are selectable in the dropdown component.
    options: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.shape({
                value: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                    PropTypes.bool,
                ]),
                label: PropTypes.node,
            }),
        ]),
    ),

    // Whether this select is in a loading state, if true a circular progress will be shown.
    loading: PropTypes.bool,

    error: PropTypes.string,

    SelectDisplayProps: PropTypes.object,

    FormControlProps: PropTypes.object,

    disabled: PropTypes.bool,
};

AdminAutocomplete.defaultProps = {
    // If no type is specified, default to large.
    type: SIZE_LARGE,

    loading: false,

    disabled: false,
};

export default withStyles(styles)(AdminAutocomplete);
